<template>
  <ValidationObserver
    v-slot="{ invalid }"
    tag="form"
    @submit="onSubmit($event)"
  >
    <b-modal
      id="compose-message-modal"
      data-cy="compose-message-modal"
      class="compose-message-modal"
      title="New Mass Text Message"
      centered
      size="lg"
      @show="onShow"
      @hidden="onHidden"
    >
      <div>
        <b-alert
          v-if="recipientType === 'group' && selectedCustomers.length"
          show
          variant="info"
        >
          {{
            `${selectedCustomers.length} ${customerLabelWithParens(null, currentMerchant)} selected`
          }}
        </b-alert>
        <span>
          This message will appear in each of the
          {{ customersLabelLowercase(null, currentMerchant) }}' feeds. <br />
          The {{ customersLabelLowercase(null, currentMerchant) }} will not know
          who else you sent this message to.
        </span>
      </div>
      <b-card id="chat-window" class="v-flex" no-body>
        <div id="snippet-triggers">
          <div id="template-select">
            <span>Add Text From Template:</span>
            <ModelSelect
              id="message-templates"
              class="template-model-select"
              data-cy="message-templates"
              :options="templateOptions"
              placeholder="Choose a template"
              :value="selectedTemplateOption"
              @input="onTemplatePick"
            />
          </div>
        </div>
        <span v-if="recipientType === 'csv'">
          Total {{ customersLabel }} Count: {{ csvData.length }}
        </span>
        <div id="recipient-option-container">
          <div v-if="recipientType === 'group'" class="group-select">
            <ValidationProvider rules="required">
              <b-form-input
                v-model="batchName"
                placeholder="Batch name (Email subject)"
                data-cy="batch-name"
              />
            </ValidationProvider>
          </div>
          <div
            v-else-if="recipientType === 'csv'"
            id="csv-upload-container"
            class="d-flex"
          >
            <b-form-file
              v-model="csvFile"
              :disabled="!!selectedCustomers.length"
              class="import-csv"
              placeholder="Choose a file or drop it here..."
              browse-text="Import CSV"
              data-cy="import"
              @input="onFile($event)"
            />
            <b-form-select
              id="csv-key-select"
              v-model="csvDataKey"
              :disabled="!csvFile"
            >
              <option :value="''" disabled>
                -- {{ customerLabel }} external ID --
              </option>
              <option
                v-for="(value, key) of csvData[0]"
                :key="key"
                :value="key"
              >
                {{ key }}
              </option>
            </b-form-select>
          </div>

          <div v-if="recipientType === 'csv'" id="recipient-select">
            <div class="d-flex">
              <div id="group-name-input">
                <ValidationProvider rules="required">
                  <b-form-input
                    v-model="batchName"
                    placeholder="Batch name (Email subject)"
                    data-cy="batch-name"
                  />
                </ValidationProvider>
              </div>
            </div>
          </div>
        </div>

        <div id="channel-schedule-container">
          <ValidationProvider rules="required" name="channels">
            <b-form-checkbox-group
              v-model="selectedChannels"
              class="group-options"
              :options="channelOptions"
              name="channel-checkbox"
              data-cy="channel-select"
            />
          </ValidationProvider>

          <div id="schedule-container">
            <b-form-checkbox
              v-model="scheduleForLater"
              data-cy="schedule-toggle"
            >
              Schedule for later
            </b-form-checkbox>
            <ValidationProvider
              :rules="scheduleForLater ? 'required' : ''"
              name="send time"
              class="schedule-picker"
            >
              <DateTimePicker
                :value="scheduledAt ? scheduledAt.toISOString() : ''"
                :is-date-time="true"
                :editing="scheduleForLater"
                :is24hr="false"
                :valid-hours="{ min: 8, max: 20 }"
                :minute-increment="15"
                :min-date="minDate"
                input-class="form-control"
                placeholder="Send Time"
                name="scheduled-at"
                data-cy="scheduled-at"
                :on-change="onScheduledAtChange"
              />
            </ValidationProvider>
          </div>
        </div>

        <template #footer>
          <b-form
            id="chat-input"
            :class="{ 'chat-input-disabled': !canFreeType }"
          >
            <div class="mt-2 d-flex justify-content-between">
              <div class="d-flex align-items-center">
                <SnippetTrigger
                  id="payment-link"
                  label="Payment Link"
                  type="dark"
                  data-cy="payment-link-snippet"
                  @click.native="addPaymentLink()"
                />
                <label
                  class="attach-button btn btn-sm ml-2"
                  for="group-attach-file"
                  role="button"
                  tabindex="0"
                  title="Attach an image or PDF"
                  aria-label="Attach an image or PDF"
                  data-cy="group-attachment-button"
                  @keydown.enter.prevent="openAttachmentPicker"
                  @keydown.space.prevent="openAttachmentPicker"
                >
                  <Paperclip :size="20" />
                </label>
                <div class="d-none">
                  <b-form-file
                    id="group-attach-file"
                    ref="attachmentInput"
                    :accept="attachmentAccept"
                    data-cy="group-attachment-input"
                    @input="onAttachmentPick"
                  />
                </div>
              </div>
              <div class="button-actions d-flex align-items-center">
                <BlytzIconInfo
                  title="Recipients receive what's below (with certain values populated)"
                  class="chat-info"
                  :size="20"
                  color="#ffffff"
                />

                <div
                  v-if="!canFreeType"
                  class="display-inline-block clear-chat-container"
                >
                  <b-button class="clear-chat" small @click="clearChat">
                    <CircleX :size="20" />
                  </b-button>
                </div>
              </div>
            </div>
            <div
              v-if="showComplianceBlock"
              class="card-warning-block"
              role="alert"
              aria-live="assertive"
              data-cy="chat-compliance-error"
            >
              <div class="card-warning-head">
                <ShieldAlert :size="18" :stroke-width="1.75" />
                <span class="card-warning-title">Message not sent</span>
              </div>
              <p class="card-warning-body">{{ cardComplianceError }}</p>
              <div class="card-warning-actions">
                <button
                  type="button"
                  class="card-warning-dismiss"
                  @click="dismissComplianceBlock"
                >
                  Got it
                </button>
              </div>
            </div>
            <div
              v-else-if="showComplianceHint"
              class="card-warning-hint"
              aria-live="polite"
              data-cy="chat-compliance-hint"
            >
              <TriangleAlert :size="16" :stroke-width="1.5" />
              <span>{{ complianceHint }}</span>
            </div>
            <div
              v-if="attachment"
              class="group-attachment d-flex align-items-start mt-2"
              data-cy="group-attachment"
            >
              <b-button
                size="sm"
                class="mr-2"
                title="Remove attachment"
                data-cy="group-attachment-remove"
                @click="clearAttachment"
              >
                <CircleX :size="20" />
              </b-button>
              <img
                v-if="attachmentPreviewUrl"
                class="group-attachment-thumb"
                :src="attachmentPreviewUrl"
                :alt="attachment.name"
                data-cy="group-attachment-preview"
              />
              <span v-else class="group-attachment-name">
                <FileText :size="20" />
                {{ attachment.name }}
              </span>
            </div>
            <b-alert
              v-if="showMmsNote"
              show
              variant="info"
              class="mt-2 mb-2"
              data-cy="group-attachment-mms-note"
            >
              Texts with an attachment send as MMS, which costs more than SMS.
            </b-alert>
            <ValidationProvider rules="required">
              <b-textarea
                id="chat-input-text"
                v-model="messageBody"
                :class="{ 'chat-input-blocked': showComplianceBlock }"
                :placeholder="chatDisplay"
                :maxlength="1500"
                no-resize
                :disabled="!canFreeType"
              />
            </ValidationProvider>
            <small v-if="messageBody.length >= 1500" class="chat-limit-warning">
              Message limited to 1500 characters.
            </small>
          </b-form>
        </template>
      </b-card>

      <template #modal-footer>
        <div id="save-template">
          <b-checkbox
            v-if="canUpdateTemplates"
            :checked="isSaveTemplate"
            :disabled="!!attachment"
            data-cy="save-template-toggle"
            @change="toggleSaveTemplate"
          >
            Save as New Template
          </b-checkbox>
          <small
            v-if="canUpdateTemplates && attachment"
            class="text-muted"
            data-cy="save-template-attachment-hint"
          >
            Templates can't hold attachments, so Save as New Template is off.
          </small>
          <div v-if="isSaveTemplate" role="group">
            <ValidationProvider
              :rules="`required|valid-template:${validTemplateName}`"
            >
              <b-form-input
                id="input-live"
                v-model="currentTemplateName"
                :state="validTemplateName"
                placeholder="Template name"
                @change="onTemplateName"
              />
              <b-form-invalid-feedback id="input-live-feedback">
                Existing template name
              </b-form-invalid-feedback>
            </ValidationProvider>
          </div>
        </div>
        <b-button
          id="compose-message-send"
          type="submit"
          :disabled="preventSubmit(invalid)"
          data-cy="send"
          @click="onSubmit"
        >
          <span v-if="!isBusy">{{ !canClose ? 'Send' : 'Close' }}</span>
          <b-spinner v-else small />
        </b-button>
      </template>
    </b-modal>
  </ValidationObserver>
</template>

<script>
import { AuthMixin } from '@/mixins/AuthMixin/AuthMixin'
import {
  checkCardCompliance,
  CARD_COMPLIANCE_HINTS,
  CARD_COMPLIANCE_MESSAGES
} from '@/utils/card-compliance'
import { reportBlockedSend } from '@/utils/blocked-send-report'
import { Component, Vue, Mixins, Prop, Watch } from 'vue-property-decorator'
import { mapGetters } from 'vuex'
import {
  customersLabelLowercase,
  customerLabelWithParens
} from '@/utils/platformLabels.js'
import Papa from 'papaparse'
import SnippetTrigger from '@/components/SnippetTrigger/SnippetTrigger/SnippetTrigger.vue'
import { uniqBy } from 'lodash-es'
import { extend, ValidationObserver, ValidationProvider } from 'vee-validate'
import VueMoment from 'vue-moment'
import {
  CircleX,
  FileText,
  Paperclip,
  ShieldAlert,
  TriangleAlert
} from 'lucide-vue'
import BlytzIconInfo from '@/components/Icons/BlytzIconInfo.vue'
import DateTimePicker from '@/components/DateTimePicker/DateTimePicker.vue'
import { ModelSelect } from 'vue-search-select'
import { cfAxios } from '@/utils/init-axios.js'
import {
  NO_TEMPLATE_CHOICE,
  resolveTemplatePick,
  templateChoices,
  templateMessageText
} from '@/utils/templatePick'
import {
  GROUP_ATTACHMENT_ACCEPT,
  groupAttachmentError,
  readGroupAttachment
} from '@/utils/groupMessageAttachment'

Vue.use(VueMoment)

extend('valid-template', {
  validate(value, { is_valid }) {
    return value && is_valid === 'null' ? true : false
  },
  params: ['is_valid'],
  message: 'Must be empty or 4 digits'
})

@Component({
  computed: {
    ...mapGetters(['currentMerchant', 'customersLabel', 'customerLabel'])
  },
  components: {
    BlytzIconInfo,
    SnippetTrigger,
    ValidationObserver,
    ValidationProvider,
    CircleX,
    FileText,
    Paperclip,
    ShieldAlert,
    TriangleAlert,
    DateTimePicker,
    ModelSelect
  }
})
export default class ComposeMessage extends Mixins(AuthMixin) {
  @Prop({ default: [] }) customers
  @Prop({ default: [] }) selectedRecipients

  attachment = null
  attachmentAccept = GROUP_ATTACHMENT_ACCEPT
  attachmentPreviewUrl = null
  batchName = ''
  canClose = false
  csvFile = null
  csvData = []
  csvDataKey = ''
  currentTemplateName = ''
  currentTemplate = null
  isBusy = false
  isSaveTemplate = false
  messageBody = ''
  messageTemplates = []
  selectedChannels = ['TEXT']
  channelOptions = [
    { text: 'Text', value: 'TEXT' },
    { text: 'Email', value: 'EMAIL' }
  ]
  scheduleForLater = false
  scheduledAt = null
  minDate = null
  selectedCustomers = []
  selectedTemplateOption = NO_TEMPLATE_CHOICE
  validTemplateName = null

  get templateOptions() {
    return templateChoices(this.messageTemplates)
  }

  // Bound to `input` rather than `v-model` on purpose: the control keeps
  // showing the template it loaded, so re-picking it emits an unchanged option
  // and a watcher would never fire. See utils/templatePick.
  onTemplatePick(option) {
    const { choice, template } = resolveTemplatePick(
      this.messageTemplates,
      option
    )

    this.selectedTemplateOption = choice
    // A pick replaces the body rather than appending to it: the box holds the
    // template the control names, so anything typed or previously loaded goes.
    if (template) this.messageBody = templateMessageText(template)
  }

  customersLabelLowercase(account, merchant) {
    return customersLabelLowercase(account, merchant)
  }

  customerLabelWithParens(account, merchant) {
    return customerLabelWithParens(account, merchant)
  }

  mounted() {
    this.getMessageTemplates()
  }

  beforeDestroy() {
    this.releasePreview()
  }

  @Watch('csvFile')
  clearCSV(newVal) {
    if (newVal) return
    this.csvFile = null
    this.csvData = []
    this.csvDataKey = ''
  }

  async addPaymentLink() {
    this.messageBody = this.messageBody + ' {{ payment_link }}'
  }

  clearChat() {
    this.messageBody = ''
    // The picker names the template whose text is in the box. Emptying the box
    // has to empty the picker too, or it keeps crediting text that is gone.
    this.selectedTemplateOption = NO_TEMPLATE_CHOICE
  }

  openAttachmentPicker() {
    this.$refs.attachmentInput?.$el?.querySelector?.('input')?.click()
  }

  onAttachmentPick(file) {
    if (!file) return
    const error = groupAttachmentError(file)
    if (error) {
      // Keep any file already attached; only the refused pick is dropped.
      this.$refs.attachmentInput?.reset?.()
      this.$toasted.global.error({ message: error })

      return
    }
    this.releasePreview()
    this.attachment = file
    // The template box is disabled while a file is attached, so turn it off rather than leave it set.
    if (this.isSaveTemplate) {
      this.toggleSaveTemplate()
      this.validTemplateName = null
    }
    if (file.type.startsWith('image/')) {
      this.attachmentPreviewUrl = URL.createObjectURL(file)
    }
  }

  releasePreview() {
    if (this.attachmentPreviewUrl)
      URL.revokeObjectURL(this.attachmentPreviewUrl)
    this.attachmentPreviewUrl = null
  }

  clearAttachment() {
    this.releasePreview()
    this.attachment = null
    this.$refs.attachmentInput?.reset?.()
  }

  get showMmsNote() {
    return Boolean(this.attachment) && this.selectedChannels.includes('TEXT')
  }

  clearSelectedCustomers() {
    this.selectedCustomers = []
  }

  async getMessageTemplates() {
    try {
      const response = await cfAxios.get(
        this.$endpoints.merchantMessageTemplates(this.currentMerchant?.pkid)
      )
      // Quick Send writes only `chat_visible`; `active` is a legacy flag it no longer sets.
      const quickSendTemplates = response.data.templates.filter(
        template => template.chat_visible
      )
      this.messageTemplates = quickSendTemplates.map(template => {
        return { value: template, text: template.name }
      })
    } catch (err) {
      console.log('error', err)
    }
  }

  onFile(file) {
    if (!file) return
    const config = {
      header: true,
      complete: results => {
        this.csvData = results.data
      },
      error: err => {
        console.log(err)
      },
      skipEmptyLines: true
    }
    Papa.parse(file, config)
  }

  onHidden() {
    this.clearCSV()
    this.clearSelectedCustomers()
    this.clearChat()
    this.batchName = ''
    this.isSaveTemplate = false
    this.validTemplateName = null
    this.canClose = false
    this.selectedChannels = ['TEXT']
    this.scheduleForLater = false
    this.scheduledAt = null
    this.clearAttachment()
  }

  onShow() {
    this.minDate = new Date()
    if (this.selectedRecipients.length) {
      this.selectedCustomers = uniqBy(this.selectedRecipients, 'customerID')
    }
  }

  toggleSaveTemplate() {
    this.isSaveTemplate = !this.isSaveTemplate
    this.currentTemplateName = ''
  }

  onScheduledAtChange(value) {
    this.scheduledAt = value ? new Date(value) : null
  }

  // Same PCI guard as Chat.vue: a mass text that asks for card details or carries a card
  // number goes to every recipient, so it is refused here as well as on the button.
  get cardComplianceViolation() {
    return checkCardCompliance(this.messageBody)
  }

  get cardComplianceError() {
    const violation = this.cardComplianceViolation

    return violation ? CARD_COMPLIANCE_MESSAGES[violation] : null
  }

  get complianceHint() {
    const violation = this.cardComplianceViolation

    return violation ? CARD_COMPLIANCE_HINTS[violation] : null
  }

  sendBlocked = false

  get showComplianceBlock() {
    return this.sendBlocked && Boolean(this.cardComplianceViolation)
  }

  get showComplianceHint() {
    return Boolean(this.cardComplianceViolation) && !this.sendBlocked
  }

  @Watch('cardComplianceViolation')
  onCardComplianceViolation(violation) {
    if (!violation) this.sendBlocked = false
  }

  dismissComplianceBlock() {
    this.sendBlocked = false
  }

  // A refused Send is reported so the account team hears about it (Slack, via messaging-service).
  // Once per refusal; editing back to clean text clears sendBlocked, so the next refusal reports.
  blockSend() {
    if (this.sendBlocked) return
    this.sendBlocked = true
    reportBlockedSend({
      merchantId: this.currentMerchant?.pkid ?? this.currentMerchant?.id,
      violation: this.cardComplianceViolation,
      message: this.messageBody,
      composer: 'group'
    })
  }

  async onSubmit() {
    if (this.canClose) return this.$bvModal.hide('compose-message-modal')
    if (this.cardComplianceError) {
      this.blockSend()

      return
    }

    this.isBusy = true

    const customerIDs = this.csvFile
      ? this.csvData
          .map(customer => customer[this.csvDataKey])
          .filter(customer => customer)
      : this.selectedCustomers.map(customer => customer.customerID)

    const channels = this.selectedChannels

    const sendAt =
      this.scheduleForLater && this.scheduledAt
        ? this.scheduledAt.toISOString()
        : null

    const merchantId = this.currentMerchant?.pkid ?? this.currentMerchant?.id

    const file = this.attachment
    let posted = false

    try {
      const attachment = file ? await readGroupAttachment(file) : null
      posted = true
      await cfAxios.post(
        `/messaging/v1/merchant/${merchantId}/group-messages`,
        {
          audience: {
            customers: customerIDs,
            isCsvImported: !!this.csvData?.length
          },
          channels,
          sendAt,
          name: this.batchName,
          message: this.messageBody,
          saveAsTemplate: this.isSaveTemplate,
          ...(attachment ? { attachment } : {})
        }
      )
      this.canClose = true
    } catch (err) {
      console.log(err)
      // The legacy flow reports success either way; with a file, say what is known instead.
      if (file) {
        const status = err?.response?.status
        if (!posted || (status >= 400 && status < 500)) {
          this.isBusy = false
          this.$toasted.global.error({
            message:
              err?.response?.data?.message ??
              'The message with the attachment was not sent. Please try again.'
          })

          return
        }
        // A timeout, network error or 5xx may still have created the batch: no blind retry.
        this.$toasted.global.error({
          message:
            "We couldn't confirm the send. Check the Message Report before sending again."
        })
        this.closeAfterSend()

        return
      }
    }

    this.$toasted.show(
      `Message group now processing for ${customerIDs.length} customer(s)`,
      {
        duration: 5000,
        position: 'bottom-center'
      }
    )
    this.closeAfterSend()
  }

  closeAfterSend() {
    this.isBusy = false
    this.clearCSV()
    this.$emit('clearCustomers')
    this.$bvModal.hide('compose-message-modal')
  }

  onTemplateName() {
    const templateNames = this.messageTemplates.map(template => {
      return template.text
    })
    if (templateNames.includes(this.currentTemplateName)) {
      this.isBusy = false
      this.validTemplateName = false

      return
    }
    this.validTemplateName = null
  }

  // A compliance error does not disable the button: the Send attempt is what swaps the hint
  // for the block panel, so onSubmit refuses instead.
  preventSubmit(invalid) {
    if (this.isBusy) return this.isBusy
    if (this.csvFile) return !(!invalid && this.csvDataKey)
    if (this.selectedCustomers.length) return invalid

    return true
  }

  get recipientType() {
    return this.selectedCustomers.length ? 'group' : 'csv'
  }

  get canFreeType() {
    if (this.$store.getters.currentMerchant.check_custom_roles) {
      return this.hasPermission([
        'super_admin',
        'admin',
        'is_customer_free_text_allowed'
      ])
    } else {
      return this.hasPermission(['super_admin', 'admin', 'staff'])
    }
  }

  get canUpdateTemplates() {
    if (this.$store.getters.currentMerchant.check_custom_roles) {
      return this.hasPermission([
        'super_admin',
        'admin',
        'is_template_update_allowed'
      ])
    } else {
      return this.hasPermission(['super_admin', 'admin', 'staff'])
    }
  }

  get chatDisplay() {
    return this.canFreeType
      ? 'Enter your message...'
      : 'Choose a template to chat...'
  }
}
</script>

<style lang="scss">
/* stylelint-disable */
.multiselect {
  min-height: 38px;

  .multiselect__tags {
    .multiselect__tag {
      background: $button-blue;

      i:hover {
        background: darken($button-blue, 10%);
      }

      i::after {
        color: white;
      }
    }
  }

  .multiselect__option--highlight {
    background: lighten($blue, 25%);

    &:after {
      background: lighten($blue, 25%);
    }
  }
}

.multiselect__select {
  height: 38px;
}

.multiselect__tags {
  height: 38px;
  min-height: 38px;

  border: 1px solid #ced4da;
}

div.multiselect__tags-wrap {
  display: none;
}

#compose-message-modal {
  .btn-primary {
    width: 20%;

    background-color: $button-blue;
  }
}

.group-options {
  .custom-control-input:checked ~ .custom-control-label::before {
    border-color: #304258;
    background-color: #304258;
  }
}
</style>
<style lang="scss" scoped>
#snippet-triggers {
  width: 100%;
  font-size: 0.9rem;
}

#template-select {
  margin-top: 20px;
  margin-bottom: 20px;

  span {
    float: left;
    margin-bottom: 5px;

    font-size: 16px;
  }

  // Sits under its floated label at full width, where the old select sat, so
  // it lines up with the group select below it.
  .template-model-select.ui.selection.dropdown {
    clear: both;
    width: 100%;
    min-height: 38px;

    display: flex;
    align-items: center;

    border-radius: 4px;

    .menu {
      max-height: 15rem;
    }
  }
}

#recipient-option-container {
  .group-select {
    display: flex;
    gap: 10px;
    margin-bottom: 1rem;

    > * {
      flex: 1;
    }

    .custom-checkbox {
      width: 20%;
      margin-left: 1rem;
    }

    .clear-customers {
      display: flex;
      align-items: center;
      justify-content: center;
      height: 38px;

      svg {
        width: 1.25em;
        height: 1.25em;

        &:hover {
          cursor: pointer;
        }
      }

      .enabled-action {
        color: $red;

        &:hover {
          color: darken($red, 15%);
        }
      }

      .disabled-action {
        color: lighten($red, 20%);

        &:hover {
          cursor: default;
        }
      }
    }
  }

  #recipient-select {
    margin: 10px 0 15px;

    > div {
      align-items: center;
    }

    #group-name-input {
      display: inline-block;
      width: 40%;
      padding: 0 1rem 0 0;
    }

    #clear-btn {
      margin-right: 10px;
    }
  }

  #csv-upload-container {
    .import-csv {
      width: 70%;
      margin-right: 10px;
    }

    #csv-key-select {
      width: 30%;
    }
  }
}

#channel-schedule-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 15px;

  .group-options {
    margin-left: 10px;
  }

  #schedule-container {
    display: flex;
    align-items: center;
  }

  .schedule-picker {
    margin-left: 8px;
  }
}

#msg-type-btns {
  margin-bottom: 30px;

  .btn {
    margin-right: 10px;
  }

  .btn:not(:first-child) {
    margin-left: 10px;
  }
}

#save-template {
  position: relative;
  right: 20%;
  width: 70%;
  display: flex;
  align-items: center;

  .custom-checkbox {
    display: inline-block;
    margin-right: 10px;
    width: 40%;
  }
}

#chat-window {
  width: 100%;
  max-height: 100%;

  border-style: none;

  .card {
    flex: 1;
    max-height: 100%;

    border-style: none;
    border-radius: 0;
  }

  .card-footer {
    height: 330px;
    padding: 24px;

    background: #455567;
    border-top: 1px solid #6a84a0;
  }

  #chat-input {
    position: relative;

    display: flex;
    flex-direction: row;
    flex-direction: column;
    height: 100%;

    background-color: #373c42;
    border-radius: 0.25rem;

    transition:
      background-color 0.2s ease,
      box-shadow 0.2s ease;

    // Sized to its label: a percentage width made the label spill under the paperclip beside it.
    #payment-link {
      flex: none;
      margin-left: 10px;
    }

    .attach-button {
      // SnippetTrigger's pill carries a 10px bottom margin; matching it centres the two on one line.
      margin-bottom: 10px;
      color: $customer-detail-text-color;

      &:hover,
      &:focus-visible {
        background-color: rgba(255, 255, 255, 0.3);
      }
    }

    #language {
      width: 22%;
      margin-left: 20px;

      border-radius: 25px;
    }

    #chat-info {
      padding: 0.15em;
      border-radius: 100%;
      margin: 0 10px 0 auto;

      svg {
        margin: 0;
      }
    }

    span {
      height: 100%;
    }

    #chat-input-text {
      flex: 1;
      width: calc(100% - 2rem);
      height: inherit;
      overflow: auto;

      color: white;
      font-size: 14px;
      line-height: 1.33;
      white-space: pre-wrap;
      word-wrap: break-word;

      background-color: transparent;
      border-style: none;
      outline: none;
      -webkit-font-smoothing: antialiased;

      &::placeholder {
        color: #ccc;
      }
    }

    #chat-input-text:empty::before {
      display: block;

      outline: none;
      cursor: text;
      filter: contrast(15%);

      content: attr(placeholder);
    }

    .actions {
      position: absolute;
      bottom: 0;
      left: 0;

      display: flex;
      justify-content: flex-end;
      width: 100%;
      padding: 0 1rem;

      .btn {
        color: $customer-detail-text-color;

        background-color: rgba(255, 255, 255, 0);
        border-color: transparent;

        &:hover {
          background-color: rgba(255, 255, 255, 0.3);
        }
      }
    }
  }

  .chat-limit-warning {
    display: block;
    padding: 0.25rem 0.5rem;
    color: #f5a623;
    font-size: 12px;
  }

  // Card-details warning, two stages (BP-2970); mirrors Chat.vue.
  .card-warning-hint {
    display: flex;
    gap: 8px;
    align-items: center;
    margin-bottom: 10px;
    padding: 7px 12px;
    color: #ffd75e;
    font-size: 13px;
    line-height: 17px;
    background: rgba($yellow, 0.14);
    border: 1px solid rgba($yellow, 0.45);
    border-radius: 0.25rem;

    svg {
      flex-shrink: 0;
    }
  }

  .card-warning-block {
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-bottom: 12px;
    padding: 12px 14px;
    background: $customer-detail-bg;
    border: 1px solid $customer-detail-chat-divider-color;
    border-left: 3px solid $red;
    border-radius: 0.25rem;
    animation: card-warning-enter 200ms cubic-bezier(0, 0, 0.2, 1) both;

    .card-warning-head {
      display: flex;
      gap: 8px;
      align-items: center;
      color: #ff6fa8;

      svg {
        flex-shrink: 0;
      }
    }

    .card-warning-title {
      color: #fff;
      font-family: $accent-font-family;
      font-size: 15px;
      font-weight: 700;
      line-height: 18px;
    }

    .card-warning-body {
      margin: 0;
      color: #dfe6ee;
      font-size: 13px;
      line-height: 18px;
      text-wrap: pretty;
    }

    .card-warning-actions {
      display: flex;
      justify-content: flex-end;
    }

    .card-warning-dismiss {
      padding: 5px 14px;
      color: #fff;
      font-size: 13px;
      font-weight: 500;
      background: $button-blue;
      border: 1px solid $button-blue;
      border-radius: 0.25rem;

      &:hover {
        background: $hover-button-blue;
        border-color: $hover-button-blue;
      }
    }
  }

  .chat-input-blocked {
    box-shadow: inset 0 0 0 1px $red;
  }

  @keyframes card-warning-enter {
    from {
      opacity: 0;
      transform: translateY(6px);
    }

    to {
      opacity: 1;
      transform: none;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .card-warning-block {
      animation: none;
    }
  }

  .chat-input-disabled {
    background: transparent !important;
  }
}

.clear-chat-container {
  float: right;
  padding: 0.2em 1em 0 0;

  button {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0.1em;
    background: none;
    border: none;

    svg {
      width: 1.5rem;
      height: 1.5rem;
    }
  }
}
.group-attachment-thumb {
  max-width: 120px;
  max-height: 80px;
}

.group-attachment-name {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
</style>

<style src="vue-search-select/dist/VueSearchSelect.css" />
